import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ErreurApi, type Referentiel } from "../api/client";
import { Chargement, EnTetePage, Indicateur, Message, Vide } from "../composants/Elements";
import { formaterDate, formaterNombre } from "../domaine/formats";

/**
 * Référentiels comptables chargés sur la plateforme.
 *
 * Un référentiel est un objet de données versionné et daté : plan de comptes,
 * rôle des classes, et à terme modèles d'états et formats de déclaration.
 * Aucune de ces règles n'est écrite dans le code — c'est la condition pour que
 * la plateforme reste multi-référentielle.
 */
export function PageReferentiels() {
  const { data, isPending, error } = useQuery({
    queryKey: ["referentiels"],
    queryFn: api.referentiels,
  });

  if (error != null) {
    return (
      <>
        <EnTetePage titre="Référentiels comptables" />
        <Message type="erreur" titre={error instanceof ErreurApi ? error.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{error.message}</p>
        </Message>
      </>
    );
  }

  if (isPending) return <Chargement quoi="les référentiels" />;

  return (
    <>
      <EnTetePage
        titre="Référentiels comptables"
        description="Le plan de comptes, le rôle de chaque classe et les modèles d'états sont des données, jamais du code. Un dossier peut changer de référentiel à une date d'effet sans que les exercices antérieurs cessent d'être restituables."
      />

      {data.length === 0 ? (
        <Vide>
          Aucun référentiel chargé. Vérifier que les migrations Flyway se sont
          appliquées sur la base.
        </Vide>
      ) : (
        data.map((referentiel) => <FicheReferentiel key={referentiel.id} referentiel={referentiel} />)
      )}
    </>
  );
}

function FicheReferentiel({ referentiel }: { referentiel: Referentiel }) {
  return (
    <div className="carte">
      <h2>
        <Link to={`/plan/${referentiel.code}`}>{referentiel.code}</Link>{" "}
        <span className="discret-texte">{referentiel.libelle}</span>
      </h2>

      <div className="grille">
        <Indicateur libelle="Pays" valeur={referentiel.pays} />
        <Indicateur libelle="Version" valeur={referentiel.version} />
        <Indicateur libelle="En vigueur le" valeur={formaterDate(referentiel.dateEffet)} />
        <Indicateur
          libelle="Comptes"
          valeur={formaterNombre(referentiel.nombreDeComptes)}
          detail={`${referentiel.classes.length} classes définies`}
        />
      </div>

      <h3>Classes</h3>
      <p className="discret-texte" style={{ margin: "0 0 10px" }}>
        Le rôle commande le sort du compte à la clôture : les comptes de bilan
        sont repris à l'ouverture de l'exercice suivant, les comptes de gestion
        sont soldés.
      </p>
      <div className="tableau-conteneur">
        <table>
          <thead>
            <tr>
              <th className="nombre">Classe</th>
              <th>Libellé</th>
              <th>Rôle</th>
            </tr>
          </thead>
          <tbody>
            {referentiel.classes.map((classe) => (
              <tr key={classe.chiffre}>
                <td className="nombre">{classe.chiffre}</td>
                <td>{classe.libelle}</td>
                <td>
                  <span
                    className={`etiquette ${classe.role === "Bilan" ? "info" : "neutre"}`}
                  >
                    {classe.role}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
